// lib/analytics/google-ads.ts
//
// Google Ads conversion tracking — config, consent read, and the fire helper.
//
// WHY THIS IS CONSENT-FIRST, NOT A PASTED SNIPPET
// -----------------------------------------------
// Every Google tag (GA, Ads, GTM) was REMOVED from this site on 2026-06-24
// (commit b71d9a1, "Go-live legal: remove pre-consent trackers") because they
// loaded and set cookies before the user had consented — a PECR problem. So the
// tag is rebuilt here on the opposite default: `components/google-ads-tag.tsx`
// does not inject gtag.js at all until marketing consent is stored, and this
// module refuses to fire without it. Do NOT "simplify" this by dropping the
// snippet into app/layout.tsx; that recreates exactly what was deleted.
//
// THE TRADE-OFF THAT BUYS, STATED PLAINLY
// ---------------------------------------
// Loading only after consent means a visitor who never accepts is never
// measured — their conversion is lost, not modelled. Google's alternative
// (Consent Mode "advanced": load always, defaults denied, send cookieless
// pings) would preserve modelling, but it still contacts Google on every page
// for users who have consented to nothing, which is the judgement this site
// has already made once and made in the other direction. The campaign bids on
// Maximize Clicks, where conversion VOLUME does not drive bidding, so
// under-counting costs reporting accuracy and not spend efficiency.
//
// Consent Mode v2 signals are still set explicitly (denied by default, then
// updated) so Google's own state is correct and so switching to advanced mode
// later is a one-line change in the tag component rather than a rethink.

/** Google Ads account 523-219-6542 (ComplyRx). */
export const GOOGLE_ADS_ID = "AW-17509777306";

/**
 * Conversion labels, keyed by the moment they represent. Both actions live in
 * Ads account 523-219-6542 and are DELIBERATELY ASYMMETRIC — keep them so:
 *
 *   key         Ads action        fires at                    priority
 *   trialStart  "Trial signup"    first TRACK (created:true)  PRIMARY
 *   signup      "Account signup"  email submitted             SECONDARY
 *
 * The priority split is the whole point and it is configured in the Ads UI,
 * not here. "Account signup" reports into "All conversions" only, because one
 * person who signs up AND THEN tracks would otherwise count twice and inflate
 * the headline number. At single-digit monthly volume a clean number beats a
 * big one. That only works while these two fire points stay independent — do
 * not collapse them onto one label or one moment.
 *
 * They are genuinely different moments in this codebase: submitting an email
 * creates an account; tracking the first drug is what stamps
 * `tracker_access.trial_started_at`, starts the trial clock and fires the
 * welcome email.
 *
 * A null label here is a silent no-op by design, so a not-yet-created action
 * costs nothing. (`signup` sat null from 2026-08-03 until its action existed.)
 */
export const GOOGLE_ADS_CONVERSIONS: Record<ConversionKey, string | null> = {
  trialStart: "A0fSCN6yktscEJr_p51B",
  signup: "UEnzCJ-brdscEJr_p51B",
};

export type ConversionKey = "trialStart" | "signup";

/** Nominal value. Immaterial to Maximize Clicks bidding; set in Ads as £1. */
const CONVERSION_VALUE = 1;
const CONVERSION_CURRENCY = "GBP";

/** localStorage key written by components/cookie-consent.tsx. */
const CONSENT_KEY = "cookie-consent";
/** Fire-once guard, one key per conversion. */
const FIRED_PREFIX = "complyrx-ads-fired:";

interface StoredConsent {
  essential?: boolean;
  analytics?: boolean;
  marketing?: boolean;
}

function readConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    // Malformed JSON is treated as NO consent, never as consent. A parse
    // failure must not be a route to firing an ad tag.
    return null;
  }
}

/**
 * Advertising consent. Deliberately reads `marketing` and NOT `analytics` —
 * an analytics opt-in is not advertising consent, and conflating the two is
 * close to what got the previous tags removed.
 */
export function hasAdsConsent(): boolean {
  return readConsent()?.marketing === true;
}

/**
 * Fire a Google Ads conversion, if and only if every precondition holds:
 * consent granted, a label configured, gtag actually loaded, and not already
 * fired for this key. Silent on every failure path — analytics must never
 * throw into a product flow.
 *
 * Returns whether the conversion was sent, which the offline test asserts on.
 */
export function fireAdsConversion(key: ConversionKey): boolean {
  if (typeof window === "undefined") return false;

  const label = GOOGLE_ADS_CONVERSIONS[key];
  if (!label) return false; // no action created in Ads yet — see the map above
  if (!hasAdsConsent()) return false;

  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return false; // tag not loaded (no consent, or blocked)

  // Fire-once. The Ads action is set to count "One", so Google dedupes server
  // side too; this stops a re-render or a re-track from spending a request and
  // from muddying the browser-side picture.
  const firedKey = `${FIRED_PREFIX}${key}`;
  try {
    if (window.localStorage.getItem(firedKey)) return false;
  } catch {
    // localStorage unavailable (private mode): fall through and fire. A
    // possible duplicate is better than a missed conversion, and Ads dedupes.
  }

  gtag("event", "conversion", {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    value: CONVERSION_VALUE,
    currency: CONVERSION_CURRENCY,
  });

  try {
    window.localStorage.setItem(firedKey, new Date().toISOString());
  } catch {
    /* non-fatal */
  }
  return true;
}

/** Test seam. */
export const _internals = { readConsent, CONSENT_KEY, FIRED_PREFIX };
